// Phase 9.2 — Draft review + edit + release (single-PO editor).
//
// Read: GET /api/fnb/pos/{po_id}
// Edit line items (draft only) → PATCH /api/fnb/pos/{po_id}
// Release: POST /release → status=released (read-only)
// Cancel:  POST /cancel  → status=cancelled

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { api, API, detailFromError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  HoverCard, HoverCardContent, HoverCardTrigger,
} from "@/components/ui/hover-card";
import { ChevronDown, ChevronRight, Info, Trash2 } from "lucide-react";
import {
  POStatusPill, PoFormatBadge, fmtINR,
} from "./poStatusMeta";

// Phase 9.7.4 — UOMs that support fractional qty entry. Used by the
// per-outlet matrix Input's `step` attribute (fractional cells get
// 0.001 precision; whole-unit cells get 1). `distributeEvenly`
// (removed in 9.7.7) also depended on this set; the set is kept
// because the step= behavior still matters.
const FRACTIONAL_UOMS = new Set([
  "g", "gm", "gms", "gram", "grams",
  "kg", "kgs", "kilo", "kilos", "kilogram", "kilograms",
  "ml", "mls", "milliliter", "millilitre", "millilitres",
  "l", "ltr", "ltrs", "liter", "litre", "liters", "litres",
]);

const isFractionalUom = (uom) =>
  FRACTIONAL_UOMS.has(String(uom || "").trim().toLowerCase());

// Phase 9.6.4 — pretty-print a stock qty. Whole numbers drop the .0;
// fractional keeps up to 3 decimals to match backend precision.
const fmtStock = (n) => {
  const v = Number(n || 0);
  if (Number.isInteger(v)) return String(v);
  return v.toFixed(3).replace(/\.?0+$/, "");
};

export default function FnbPoDraftReview() {
  const { po_id } = useParams();
  const navigate = useNavigate();
  const [po, setPo] = useState(null);
  const [lines, setLines] = useState([]);
  const [notes, setNotes] = useState("");
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [addSearch, setAddSearch] = useState("");
  const [addOptions, setAddOptions] = useState([]);
  const [releasing, setReleasing] = useState(false);
  // Phase 9.7.2 — vendor-scoped autocomplete. Default ON when the PO
  // has a vendor snapshot. Operator can flip to search-all for the
  // rare non-linked SKU.
  const [scopeToVendor, setScopeToVendor] = useState(true);
  // Phase 9.7.5 — hydrate the ship-to `fb_locations` doc so the
  // "Ship-to" card can render the outlet's full GSTIN + address
  // (mirrors what the generated PO Excel now shows in its "From"
  // block). Fetched once per mount; safe to no-op when PO is
  // pan_india (no single ship-to).
  const [locationsById, setLocationsById] = useState({});
  useEffect(() => {
    let alive = true;
    api.get("/fnb/locations").then((res) => {
      if (!alive) return;
      const map = {};
      for (const l of res.data || []) map[l.location_id] = l;
      setLocationsById(map);
    }).catch(() => {});
    return () => { alive = false; };
  }, []);
  const shipToLoc = po?.ship_to_location_id
    ? locationsById[po.ship_to_location_id]
    : null;

  const load = useCallback(async () => {
    try {
      const res = await api.get(`/fnb/pos/${po_id}`);
      setPo(res.data);
      setLines(res.data.line_items || []);
      setNotes(res.data.notes || "");
    } catch (e) {
      toast.error(detailFromError(e));
    }
  }, [po_id]);

  useEffect(() => { load(); }, [load]);

  const vendorName = po?.vendor_snapshot?.name || "";
  // Phase 9.7.3 — track focus so we can show the browse dropdown even
  // when the search field is empty.
  const [inputFocused, setInputFocused] = useState(false);

  useEffect(() => {
    // Phase 9.7.3 — trigger the query when EITHER we have a search
    // term OR the operator has focused the empty input while a vendor
    // is scoped (browse mode). No query fires while unfocused + empty.
    const isBrowse = inputFocused && !addSearch && scopeToVendor && vendorName;
    if (!isBrowse && (!addSearch || addSearch.length < 2)) {
      setAddOptions([]);
      return;
    }
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        // Phase 9.6.4 — pass the PO id so the backend can enrich each
        // autocomplete result with current_stock_by_location + total.
        // Phase 9.7.2 — narrow to vendor-linked SKUs by default.
        // Phase 9.7.3 — support browse mode: no search term needed.
        // Phase 9.7.6 — pass exclude_in_po_id so SKUs already in the
        // draft don't appear in the Browse dropdown (they reappear
        // naturally after Remove).
        const params = new URLSearchParams();
        if (addSearch) params.set("search", addSearch);
        params.set("limit", isBrowse ? "50" : "10");
        if (po?.po_id) params.set("stock_for_po_id", po.po_id);
        if (scopeToVendor && vendorName) params.set("vendor_name", vendorName);
        if (po?.po_id) params.set("exclude_in_po_id", po.po_id);
        const res = await api.get(`/fnb/items?${params}`);
        if (!cancelled) setAddOptions(res.data.items || []);
      } catch { /* autocomplete failures degrade silently */ }
    }, 200);
    return () => { cancelled = true; clearTimeout(t); };
  }, [addSearch, po?.po_id, scopeToVendor, vendorName, inputFocused, lines.length]);

  const isDraft = po?.status === "draft";
  // Phase 9.6.4 — always render the Current Stock column unless the
  // PO has zero destinations wired (edge case). Then the column
  // header + cell degrade to "—".
  const destinations = po?.destination_locations || [];
  const showStockColumn = destinations.length > 0;
  const isMultiDest = destinations.length > 1;
  // Phase 9.7.4 — matrix editor gate. Only Pan India POs expose the
  // per-outlet expander; location_wise stays exactly as it was.
  const isPanIndia = po?.format === "pan_india";

  // Phase 9.7.4 — track which lines have their matrix editor
  // expanded. Keyed by 1-indexed `line_no` (stable across renders,
  // unlike the row index).
  const [expandedLineNos, setExpandedLineNos] = useState(() => new Set());
  const toggleLineExpand = (line_no) => {
    setExpandedLineNos((prev) => {
      const next = new Set(prev);
      if (next.has(line_no)) next.delete(line_no);
      else next.add(line_no);
      return next;
    });
  };

  // Phase 9.7.4 — per-line debounce timers for the matrix editor's
  // 400ms save-on-idle contract. Keyed by `line_no` so edits to
  // different lines don't cancel each other.
  const debounceTimersRef = useRef({});
  useEffect(() => {
    // Clear any pending timers on unmount to avoid state-after-unmount
    // network noise.
    return () => {
      Object.values(debounceTimersRef.current).forEach((t) => clearTimeout(t));
      debounceTimersRef.current = {};
    };
  }, []);

  const patchLineNow = useCallback(async (line_no, body) => {
    try {
      const res = await api.patch(`/fnb/pos/${po_id}/lines/${line_no}`, body);
      // The endpoint returns the fresh PO — snap local state to it
      // so header totals + all lines stay authoritative.
      setPo(res.data);
      setLines(res.data.line_items || []);
    } catch (e) {
      toast.error(detailFromError(e));
    }
  }, [po_id]);

  const scheduleLinePatch = useCallback((line_no, body) => {
    const existing = debounceTimersRef.current[line_no];
    if (existing) clearTimeout(existing);
    debounceTimersRef.current[line_no] = setTimeout(() => {
      delete debounceTimersRef.current[line_no];
      patchLineNow(line_no, body);
    }, 400);
  }, [patchLineNow]);

  // Update a single (line, outlet) cell — optimistic local update
  // followed by debounced PATCH of the full per_location_qty dict.
  const patchMatrixCell = (lineIdx, loc_id, rawVal) => {
    const parsed = Number(rawVal);
    const val = Number.isFinite(parsed) && parsed >= 0 ? parsed : 0;
    setLines((prev) => prev.map((l, j) => {
      if (j !== lineIdx) return l;
      const old = l.per_location_qty || {};
      const nextPerLoc = { ...old, [loc_id]: val };
      const nextQty = Object.values(nextPerLoc)
        .reduce((s, v) => s + Number(v || 0), 0);
      // Fire-and-forget the debounced backend save. Send the FULL
      // dict — the endpoint replaces `per_location_qty` wholesale.
      scheduleLinePatch(l.line_no, { per_location_qty: nextPerLoc });
      return { ...l, per_location_qty: nextPerLoc, quantity: nextQty };
    }));
  };

  // "Copy from outlet" — set every outlet on this line to the value
  // currently held at `sourceLocId`. No-op if source has no value.
  const copyFromOutlet = (lineIdx, sourceLocId) => {
    setLines((prev) => prev.map((l, j) => {
      if (j !== lineIdx) return l;
      const srcVal = Number((l.per_location_qty || {})[sourceLocId] || 0);
      const nextPerLoc = Object.fromEntries(
        destinations.map((loc) => [loc, srcVal]),
      );
      const nextQty = srcVal * destinations.length;
      scheduleLinePatch(l.line_no, { per_location_qty: nextPerLoc });
      return { ...l, per_location_qty: nextPerLoc, quantity: nextQty };
    }));
  };

  // Phase 9.7.7 — `distributeEvenly` and `clearAllOutlets` bulk
  // helpers were removed. Operators requested per-cell edits only.
  // `Copy from another outlet` (see the dropdown in the matrix
  // header) covers the remaining bulk-fill use case.

  const totals = useMemo(() => {
    let qty = 0, basic = 0, tax = 0;
    for (const l of lines) {
      const q = Number(l.quantity || 0);
      const r = Number(l.unit_rate || 0);
      const g = Number(l.gst_percent || 0);
      const b = q * r;
      qty += q; basic += b; tax += b * g / 100;
    }
    return {
      line_count: lines.length,
      total_qty: qty,
      total_basic_amount: basic,
      total_tax_amount: tax,
      total_amount: basic + tax,
    };
  }, [lines]);

  const updateLine = (i, patch) => {
    setLines((prev) => prev.map((l, j) => j === i ? { ...l, ...patch } : l));
  };
  // Phase 9.7.6 — Prominent Remove button on the row. Optimistically
  // drops the line from local state then persists via PATCH so the
  // Browse dropdown's exclude_in_po_id filter reflects the change
  // live. On failure we reload to reconcile.
  const [removingIdx, setRemovingIdx] = useState(null);
  const removeLineImmediate = async (i) => {
    const line = lines[i];
    if (!line) return;
    const label = line.item_snapshot?.name || line.item_sku;
    setRemovingIdx(i);
    const nextLines = lines.filter((_, j) => j !== i);
    setLines(nextLines);
    try {
      await api.patch(`/fnb/pos/${po_id}`, {
        line_items: nextLines.map((l) => ({
          item_sku: l.item_sku,
          item_snapshot: l.item_snapshot,
          quantity: Number(l.quantity || 0),
          unit_rate: Number(l.unit_rate || 0),
          gst_percent: Number(l.gst_percent || 0),
          mrp: l.mrp != null ? Number(l.mrp) : null,
          per_location_qty: l.per_location_qty || null,
          notes: l.notes || null,
          source_marker: l.source_marker || null,
        })),
      });
      toast.success(`Removed “${label}”`);
      // Reload so totals + line_no sequence are fresh from server.
      await load();
    } catch (e) {
      toast.error(detailFromError(e));
      await load();
    } finally {
      setRemovingIdx(null);
    }
  };
  // Phase 9.7.6 — dismissable banner when Manual PO was auto-populated
  // with a large vendor list. Rendered once, then user can dismiss.
  const [autoBannerDismissed, setAutoBannerDismissed] = useState(false);
  const showAutoPopulatedBanner = (
    po?.source === "auto_from_vendor_links"
    && lines.length > 30
    && !autoBannerDismissed
    && isDraft
  );
  const addLine = async (item) => {
    // Pre-fill unit rate from cheapest vendor rate for this item
    let unit_rate = 0, gst_percent = 0;
    try {
      const res = await api.get(`/fnb/items/${encodeURIComponent(item.sku)}`);
      const rate = (res.data.vendor_rates || []).find(
        (r) => r.vendor_id === po.vendor_id,
      ) || (res.data.vendor_rates || [])[0];
      if (rate) { unit_rate = rate.unit_rate || 0; gst_percent = rate.gst_percent || 0; }
    } catch { /* vendor rate lookup best-effort; defaults to 0/0 */ }
    setLines((prev) => [...prev, {
      item_sku: item.sku,
      item_snapshot: { name: item.name, uom: item.uom, category: item.category },
      quantity: 1, unit_rate, gst_percent,
      mrp: null, notes: null, per_location_qty: null,
      // Phase 9.6.4 — carry the autocomplete's stock snapshot forward
      // so the new row shows stock immediately (before the next PO
      // reload). Falls back to zero-filled if the autocomplete didn't
      // enrich for some reason.
      current_stock_total: item.current_stock_total ?? 0,
      current_stock_by_location: item.current_stock_by_location
        || Object.fromEntries((po?.destination_locations || []).map((l) => [l, 0])),
    }]);
    setAddSearch("");
    setAddOptions([]);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        notes,
        line_items: lines.map((l) => ({
          item_sku: l.item_sku,
          item_snapshot: l.item_snapshot,
          quantity: Number(l.quantity || 0),
          unit_rate: Number(l.unit_rate || 0),
          gst_percent: Number(l.gst_percent || 0),
          mrp: l.mrp != null ? Number(l.mrp) : null,
          per_location_qty: l.per_location_qty || null,
          notes: l.notes || null,
        })),
      };
      const res = await api.patch(`/fnb/pos/${po_id}`, body);
      setPo(res.data);
      setLines(res.data.line_items || []);
      toast.success("Draft saved");
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setSaving(false);
    }
  };

  const release = async () => {
    if (!window.confirm("Release this PO? It becomes read-only and can be sent to the vendor.")) return;
    setReleasing(true);
    try {
      const res = await api.post(`/fnb/pos/${po_id}/release`);
      setPo(res.data);
      toast.success(`PO ${res.data.po_number} released`);
    } catch (e) {
      toast.error(detailFromError(e));
    } finally {
      setReleasing(false);
    }
  };

  const cancelPO = async () => {
    if (!window.confirm("Cancel this PO?")) return;
    try {
      const res = await api.post(`/fnb/pos/${po_id}/cancel`);
      setPo(res.data);
      toast.success("PO cancelled");
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  const duplicatePO = async () => {
    try {
      const res = await api.post(`/fnb/pos/${po_id}/duplicate`);
      toast.success(`Duplicated as ${res.data.po_number}`);
      navigate(`/tools/fnb-po/pos/${res.data.po_id}`);
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  const markReceived = async () => {
    if (!window.confirm("Mark as received?")) return;
    try {
      const res = await api.post(`/fnb/pos/${po_id}/mark-received`);
      setPo(res.data);
      toast.success("Marked as received");
    } catch (e) {
      toast.error(detailFromError(e));
    }
  };

  if (!po) return <div className="text-sm text-muted-foreground" data-testid="draft-loading">Loading…</div>;

  return (
    <div className="space-y-6" data-testid="fnb-po-draft-review">
      <header className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2">
            <h1 className="text-2xl font-semibold tracking-tight font-mono" data-testid="po-number">
              {po.po_number}
            </h1>
            <POStatusPill status={po.status} testid="po-status-pill" />
            <PoFormatBadge format={po.format} />
            {po.source === "auto_from_file_rules" && (
              <span
                className="rounded-full bg-emerald-100 text-emerald-800 px-2 py-0.5 text-[10px] font-semibold"
                title="Generated from Ordering Rules file"
                data-testid="po-source-file-rules-chip"
              >
                File rules
              </span>
            )}
          </div>
          <p className="text-sm text-muted-foreground">
            Vendor: <b>{po.vendor_snapshot?.name || po.vendor_id}</b> ·{" "}
            {(po.destination_locations || []).length} destination
            {(po.destination_locations || []).length === 1 ? "" : "s"}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <a
            href={`${API}/fnb/pos/${po_id}/download`}
            className="inline-flex items-center gap-1 h-9 px-3 rounded-md border border-border/70 text-sm font-medium hover:bg-muted transition-colors"
            data-testid="po-download-btn"
          >
            ↓ Download Excel
          </a>
          <Button variant="outline" size="sm" onClick={duplicatePO} data-testid="po-duplicate-btn">
            Duplicate
          </Button>
          <Button asChild variant="ghost" size="sm">
            <Link to="/tools/fnb-po/pos">← All POs</Link>
          </Button>
        </div>
      </header>

      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        {/* Line items */}
        <Card>
          {showAutoPopulatedBanner && (
            <div
              className="mx-4 mt-4 rounded-md border border-blue-200 bg-blue-50 px-3 py-2 flex items-start justify-between gap-3"
              data-testid="auto-populate-banner"
            >
              <div className="text-xs text-blue-900">
                <span className="font-medium">Auto-added {lines.length} items linked to {vendorName || "this vendor"}.</span>
                {" "}Use the Remove button on any row to drop it.
              </div>
              <button
                type="button"
                onClick={() => setAutoBannerDismissed(true)}
                className="text-blue-900/70 hover:text-blue-900 text-xs shrink-0"
                data-testid="auto-populate-banner-dismiss"
                aria-label="Dismiss auto-add banner"
              >
                ×
              </button>
            </div>
          )}
          <CardHeader>
            <div className="flex items-center justify-between">
              <div className="text-sm font-medium">
                Line items ({lines.length})
              </div>
              {isDraft && (
                <div className="flex items-center gap-2">
                  <div className="flex items-center gap-1">
                    <span
                      className={
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] " +
                        (scopeToVendor && vendorName
                          ? "border-emerald-300 bg-emerald-50 text-emerald-800"
                          : "border-border/60 bg-muted/40 text-muted-foreground")
                      }
                      data-testid="add-line-scope-chip"
                    >
                      {scopeToVendor && vendorName
                        ? `Showing items linked to ${vendorName} only`
                        : "Showing all catalog items"}
                    </span>
                    {vendorName && (
                      <button
                        type="button"
                        onClick={() => setScopeToVendor((v) => !v)}
                        className="text-[10px] text-primary underline underline-offset-2 hover:text-primary/80"
                        data-testid="add-line-scope-toggle"
                      >
                        {scopeToVendor ? "Show all" : `Scope to ${vendorName}`}
                      </button>
                    )}
                  </div>
                  <div className="relative">
                    <Input
                      placeholder={
                        vendorName && scopeToVendor
                          ? `Browse or search items linked to ${vendorName}…`
                          : "Add item — search SKU/name…"
                      }
                      value={addSearch}
                      onChange={(e) => setAddSearch(e.target.value)}
                      onFocus={() => setInputFocused(true)}
                      onBlur={() => {
                        // 200ms delay lets the "Add" button click through
                        // before we tear the dropdown down.
                        setTimeout(() => setInputFocused(false), 200);
                      }}
                      className="text-xs w-56"
                      data-testid="add-line-search"
                    />
                    {inputFocused && !addSearch && scopeToVendor && vendorName
                      && addOptions.length === 0 && (
                      <div
                        className="absolute right-0 top-full mt-1 w-80 bg-background border border-border/70 rounded-md shadow-md z-10 p-3 text-xs"
                        data-testid="add-line-browse-empty"
                      >
                        <div className="font-medium text-sm mb-1">
                          All {vendorName} items are already in this PO.
                        </div>
                        <div className="text-muted-foreground">
                          Use{" "}
                          <button
                            type="button"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={() => setScopeToVendor(false)}
                            className="text-primary hover:text-primary/80 underline underline-offset-2"
                            data-testid="add-line-browse-empty-show-all"
                          >
                            Show all catalog
                          </button>
                          {" "}to add other items.
                        </div>
                      </div>
                    )}
                    {addOptions.length > 0 && (
                      <div className="absolute right-0 top-full mt-1 w-80 bg-background border border-border/70 rounded-md shadow-md z-10 max-h-72 overflow-auto" data-testid="add-line-options">
                        {!addSearch && scopeToVendor && vendorName && (
                          <div
                            className="sticky top-0 z-10 flex items-center justify-between bg-muted/60 backdrop-blur px-3 py-1.5 text-[10px] uppercase tracking-wider text-muted-foreground border-b border-border/70"
                            data-testid="add-line-browse-header"
                          >
                            <span>
                              Browse — {addOptions.length} items linked to <b>{vendorName}</b>
                            </span>
                            <button
                              type="button"
                              onMouseDown={(e) => e.preventDefault()}
                              onClick={() => setScopeToVendor(false)}
                              className="text-primary hover:text-primary/80 underline underline-offset-2 normal-case tracking-normal"
                              data-testid="add-line-browse-show-all"
                            >
                              Show all catalog
                            </button>
                          </div>
                        )}
                        {addOptions.map((it) => {
                          const stockNum = Number(it.current_stock_total || 0);
                          const stockShown = it.current_stock_total !== undefined;
                          const isOOS = stockShown && stockNum === 0;
                          // Phase 9.6.4 (fix) — pan-india autocomplete rows
                          // expose the per-outlet breakdown via a native
                          // `title` attr (browser tooltip). Full HoverCard
                          // inside a click-to-add <button> gets messy, so we
                          // keep the click contract simple and let users
                          // hover for the detailed picture. Once the item
                          // is added, the row's Current Stock cell shows
                          // the rich HoverCard breakdown.
                          const breakdownTitle = isMultiDest && stockShown && it.current_stock_by_location
                            ? Object.entries(it.current_stock_by_location)
                                .sort((a, b) => Number(b[1]) - Number(a[1]))
                                .map(([loc, q]) => `${loc}: ${fmtStock(Number(q || 0))}${Number(q || 0) === 0 ? " (OOS)" : ""}`)
                                .join("\n")
                            : undefined;
                          return (
                            <button
                              key={it.sku}
                              className="block w-full text-left px-3 py-1.5 text-xs hover:bg-muted"
                              onClick={() => addLine(it)}
                              data-testid={`add-line-opt-${it.sku}`}
                            >
                              <div className="font-mono text-[10px]">{it.sku}</div>
                              <div>{it.name}</div>
                              {stockShown && (
                                <div
                                  className={`text-[10px] mt-0.5 flex items-center gap-1 ${
                                    isOOS ? "text-destructive font-medium" : "text-muted-foreground"
                                  }`}
                                  data-testid={`add-line-stock-${it.sku}`}
                                  title={breakdownTitle}
                                >
                                  <span>
                                    Stock: {fmtStock(stockNum)}{" "}
                                    {isMultiDest
                                      ? `across ${destinations.length} outlets`
                                      : `at ${destinations[0]}`}
                                  </span>
                                  {isMultiDest && (
                                    <Info
                                      className="w-2.5 h-2.5 opacity-70"
                                      aria-label="Hover to see per-outlet breakdown"
                                    />
                                  )}
                                  {isOOS && (
                                    <span className="ml-1 rounded-full bg-destructive/10 text-destructive px-1.5 py-px font-semibold">
                                      OOS
                                    </span>
                                  )}
                                </div>
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {lines.length === 0 ? (
              <div className="text-sm text-muted-foreground py-6 text-center" data-testid="lines-empty">
                No line items yet. Search above to add.
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="lines-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-12">#</TableHead>
                      <TableHead>Item</TableHead>
                      {showStockColumn && (
                        <TableHead className="w-28 text-right" data-testid="col-current-stock">
                          Current Stock
                        </TableHead>
                      )}
                      <TableHead>UOM</TableHead>
                      <TableHead className="w-24 text-right">Qty</TableHead>
                      <TableHead className="w-28 text-right">Rate</TableHead>
                      <TableHead className="w-20 text-right">GST%</TableHead>
                      <TableHead className="text-right">Basic</TableHead>
                      <TableHead className="text-right">Tax</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                      {isDraft && <TableHead className="w-28 text-right">Remove</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {lines.map((l, i) => {
                      const qty = Number(l.quantity || 0);
                      const rate = Number(l.unit_rate || 0);
                      const gst = Number(l.gst_percent || 0);
                      const basic = qty * rate;
                      const tax = basic * gst / 100;
                      const stockTotal = Number(l.current_stock_total || 0);
                      const stockBreakdown = l.current_stock_by_location || {};
                      const isOOS = stockTotal === 0;
                      // Phase 9.7.4 — matrix editor plumbing
                      const line_no = l.line_no ?? (i + 1);
                      const isExpanded = isPanIndia && expandedLineNos.has(line_no);
                      const perLoc = l.per_location_qty || {};
                      const matrixColSpan = 9
                        + (showStockColumn ? 1 : 0)
                        + (isDraft ? 1 : 0);
                      const fractional = isFractionalUom(l.item_snapshot?.uom);
                      return (
                        <Fragment key={`${l.item_sku}-${i}`}>
                        <TableRow data-testid={`line-row-${i}`}>
                          <TableCell className="text-xs text-muted-foreground">{i + 1}</TableCell>
                          <TableCell>
                            <div className="flex items-start gap-1.5">
                              {isPanIndia && isDraft && (
                                <button
                                  type="button"
                                  onClick={() => toggleLineExpand(line_no)}
                                  className="mt-0.5 shrink-0 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-1 focus:ring-primary/40 rounded-sm"
                                  aria-label={isExpanded ? "Collapse per-outlet matrix" : "Expand per-outlet matrix"}
                                  aria-expanded={isExpanded}
                                  data-testid={`line-matrix-toggle-${i}`}
                                >
                                  {isExpanded
                                    ? <ChevronDown className="w-4 h-4" />
                                    : <ChevronRight className="w-4 h-4" />}
                                </button>
                              )}
                              <div className="min-w-0">
                                <div className="text-sm">{l.item_snapshot?.name || l.item_sku}</div>
                                <div className="text-[10px] font-mono text-muted-foreground">{l.item_sku}</div>
                              </div>
                            </div>
                          </TableCell>
                          {showStockColumn && (
                            <TableCell
                              className="text-right text-xs tabular-nums"
                              data-testid={`line-stock-${i}`}
                            >
                              {isMultiDest ? (
                                // Phase 9.6.4 (fix) — HoverCard (over Tooltip)
                                // because it opens on hover *and* focus/click
                                // and the trigger DOM is always present. The
                                // Info icon is the discoverability cue the
                                // tester asked for. Content is sorted
                                // descending by qty so operators see the
                                // biggest-stock outlets first — zero-stock
                                // outlets sink to the bottom with an OOS chip.
                                <HoverCard openDelay={80} closeDelay={80}>
                                  <HoverCardTrigger asChild>
                                    <button
                                      type="button"
                                      className={`inline-flex items-center gap-1 cursor-help border-b border-dotted border-muted-foreground/50 focus:outline-none focus:ring-1 focus:ring-primary/40 rounded-sm px-0.5 ${
                                        isOOS ? "text-destructive font-semibold" : ""
                                      }`}
                                      aria-label="Show per-outlet stock breakdown"
                                      data-testid={`line-stock-trigger-${i}`}
                                    >
                                      {fmtStock(stockTotal)}
                                      {isOOS && (
                                        <span
                                          className="rounded-full bg-destructive/10 text-destructive px-1.5 py-px text-[9px] font-semibold"
                                          data-testid={`line-oos-chip-${i}`}
                                        >
                                          OOS
                                        </span>
                                      )}
                                      <Info
                                        className="w-3 h-3 text-muted-foreground/70 shrink-0"
                                        aria-hidden="true"
                                        data-testid={`line-stock-info-icon-${i}`}
                                      />
                                    </button>
                                  </HoverCardTrigger>
                                  <HoverCardContent
                                    side="top"
                                    align="end"
                                    className="w-72 p-3"
                                    data-testid={`line-stock-tooltip-${i}`}
                                  >
                                    <div className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide mb-2">
                                      Stock across {destinations.length} outlets · sorted by qty
                                    </div>
                                    <div className="space-y-1">
                                      {[...destinations]
                                        .sort((a, b) =>
                                          Number(stockBreakdown[b] || 0) -
                                          Number(stockBreakdown[a] || 0))
                                        .map((loc_id) => {
                                          const q = Number(stockBreakdown[loc_id] || 0);
                                          const rowOOS = q === 0;
                                          return (
                                            <div
                                              key={loc_id}
                                              className={`flex justify-between items-center gap-3 tabular-nums text-xs ${
                                                rowOOS ? "opacity-70" : ""
                                              }`}
                                              data-testid={`stock-breakdown-row-${i}-${loc_id}`}
                                            >
                                              <span className="font-mono text-[10px] truncate">{loc_id}</span>
                                              <span className="flex items-center gap-1.5 shrink-0">
                                                <span className={rowOOS ? "text-muted-foreground" : "font-semibold"}>
                                                  {fmtStock(q)}
                                                </span>
                                                {rowOOS && (
                                                  <span className="rounded-full bg-destructive/10 text-destructive px-1.5 py-px text-[9px] font-semibold">
                                                    OOS
                                                  </span>
                                                )}
                                              </span>
                                            </div>
                                          );
                                        })}
                                    </div>
                                    <div className="mt-2 pt-2 border-t border-border/60 flex justify-between items-center text-[10px] text-muted-foreground">
                                      <span>Total</span>
                                      <span className="font-semibold tabular-nums">{fmtStock(stockTotal)}</span>
                                    </div>
                                  </HoverCardContent>
                                </HoverCard>
                              ) : (
                                <span className={`inline-flex items-center gap-1 ${
                                  isOOS ? "text-destructive font-semibold" : ""
                                }`}>
                                  {fmtStock(stockTotal)}
                                  {isOOS && (
                                    <span
                                      className="rounded-full bg-destructive/10 text-destructive px-1.5 py-px text-[9px] font-semibold"
                                      data-testid={`line-oos-chip-${i}`}
                                    >
                                      OOS
                                    </span>
                                  )}
                                </span>
                              )}
                            </TableCell>
                          )}
                          <TableCell className="text-xs">{l.item_snapshot?.uom || "—"}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-1 justify-end">
                              {isPanIndia ? (
                                // Phase 9.7.4 — Pan India qty is the SUM of
                                // per-outlet quantities. Show as read-only
                                // display; edits happen inside the matrix
                                // editor (chevron on the item cell).
                                <span
                                  className="inline-flex items-center gap-1 tabular-nums text-xs h-7 px-2 rounded-md bg-muted/40 border border-border/60 text-right min-w-[5rem] justify-end"
                                  data-testid={`line-qty-${i}`}
                                  title="Auto-computed sum of per-outlet quantities. Open the matrix editor to change."
                                >
                                  {fmtStock(qty)}
                                </span>
                              ) : (
                                <Input
                                  type="number" min={0}
                                  value={qty}
                                  onChange={(e) => updateLine(i, { quantity: e.target.value })}
                                  disabled={!isDraft}
                                  className="h-7 text-xs text-right w-20"
                                  data-testid={`line-qty-${i}`}
                                />
                              )}
                              {l.native_order && (
                                // Phase 9.7 — file-rules lines carry the
                                // native purchase-unit target ("5 pkts") on
                                // the row. Show it as a subtle secondary
                                // label with a title-tooltip explaining the
                                // measuring-unit equivalent. Operator sees
                                // "5 pkts" on the printable PO; internally
                                // stock reconciles against the quantity
                                // field above.
                                <span
                                  className="text-[10px] text-muted-foreground flex items-center gap-0.5 tabular-nums"
                                  title={`= ${fmtStock(qty)} ${l.item_snapshot?.uom || ""} (converted from ${l.native_order.qty} ${l.native_order.unit})`}
                                  data-testid={`line-native-order-${i}`}
                                >
                                  {fmtStock(l.native_order.qty)}{" "}
                                  {l.native_order.unit}
                                  <Info className="w-2.5 h-2.5 opacity-70" />
                                </span>
                              )}
                            </div>
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min={0}
                              value={rate}
                              onChange={(e) => updateLine(i, { unit_rate: e.target.value })}
                              disabled={!isDraft}
                              className="h-7 text-xs text-right w-24"
                              data-testid={`line-rate-${i}`}
                            />
                          </TableCell>
                          <TableCell>
                            <Input
                              type="number" min={0} step={0.1}
                              value={gst}
                              onChange={(e) => updateLine(i, { gst_percent: e.target.value })}
                              disabled={!isDraft}
                              className="h-7 text-xs text-right w-16"
                              data-testid={`line-gst-${i}`}
                            />
                          </TableCell>
                          <TableCell className="text-right text-xs tabular-nums">{fmtINR(basic)}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums text-muted-foreground">{fmtINR(tax)}</TableCell>
                          <TableCell className="text-right text-xs tabular-nums font-medium">{fmtINR(basic + tax)}</TableCell>
                          {isDraft && (
                            <TableCell>
                              <button
                                onClick={() => removeLineImmediate(i)}
                                disabled={removingIdx === i}
                                className="inline-flex items-center gap-1 h-7 px-2 rounded-md border border-destructive/30 text-destructive hover:bg-destructive/10 disabled:opacity-50 disabled:cursor-not-allowed text-xs font-medium transition-colors"
                                data-testid={`line-remove-btn-${i}`}
                                aria-label={`Remove ${l.item_snapshot?.name || l.item_sku}`}
                                title="Remove from PO (item will reappear in Browse)"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                                <span>Remove</span>
                              </button>
                            </TableCell>
                          )}
                        </TableRow>
                        {isExpanded && (
                          <TableRow
                            className="bg-muted/20 hover:bg-muted/20"
                            data-testid={`line-matrix-row-${i}`}
                          >
                            <TableCell colSpan={matrixColSpan} className="p-4">
                              <div className="space-y-3">
                                <div className="flex items-center justify-between flex-wrap gap-3">
                                  <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                                    Per-outlet quantities · {destinations.length} destination{destinations.length === 1 ? "" : "s"}
                                  </div>
                                  <label className="text-[11px] text-muted-foreground flex items-center gap-1.5">
                                    Copy from
                                    <select
                                      onChange={(e) => {
                                        const v = e.target.value;
                                        if (v) copyFromOutlet(i, v);
                                        e.target.value = "";
                                      }}
                                      defaultValue=""
                                      className="h-7 text-xs rounded-md border border-border/70 bg-background px-2 focus:outline-none focus:ring-1 focus:ring-primary/40"
                                      data-testid={`line-matrix-copy-from-${i}`}
                                    >
                                      <option value="" disabled>outlet…</option>
                                      {destinations.map((loc) => (
                                        <option key={loc} value={loc}>
                                          {loc} ({fmtStock(Number(perLoc[loc] || 0))})
                                        </option>
                                      ))}
                                    </select>
                                  </label>
                                </div>
                                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
                                  {destinations.map((loc) => {
                                    const cellStock = Number(stockBreakdown[loc] || 0);
                                    const cellVal = Number(perLoc[loc] || 0);
                                    // Phase 9.7.7 — per-outlet stock-status
                                    // coloring. `reorder_level_qty` is per-item
                                    // (not per-outlet) — the same threshold
                                    // is used at every destination.
                                    const reorderLevel = l.reorder_level_qty;
                                    let cellStatus; // "oos" | "reorder" | "ok" | "unconfigured"
                                    if (cellStock === 0) {
                                      cellStatus = "oos";
                                    } else if (reorderLevel == null) {
                                      cellStatus = "unconfigured";
                                    } else if (cellStock < Number(reorderLevel)) {
                                      cellStatus = "reorder";
                                    } else {
                                      cellStatus = "ok";
                                    }
                                    const cellBgClass = {
                                      oos: "bg-rose-50 border-rose-200",
                                      reorder: "bg-amber-50 border-amber-200",
                                      ok: "bg-emerald-50 border-emerald-200",
                                      unconfigured: "bg-background border-border/60",
                                    }[cellStatus];
                                    const chip = {
                                      oos: { label: "OOS", cls: "bg-rose-100 text-rose-800" },
                                      reorder: { label: "REORDER", cls: "bg-amber-100 text-amber-800" },
                                      ok: { label: "OK", cls: "bg-emerald-100 text-emerald-800" },
                                      unconfigured: { label: "UNCONFIGURED", cls: "bg-slate-100 text-slate-700" },
                                    }[cellStatus];
                                    const stockNumberCls = {
                                      oos: "text-rose-700 font-semibold",
                                      reorder: "text-amber-700 font-semibold",
                                      ok: "text-emerald-700",
                                      unconfigured: "text-muted-foreground",
                                    }[cellStatus];
                                    return (
                                      <div
                                        key={loc}
                                        className={`rounded-md border p-2 space-y-1 ${cellBgClass}`}
                                        data-testid={`line-matrix-cell-${i}-${loc}`}
                                        data-cell-status={cellStatus}
                                      >
                                        <div className="flex items-center justify-between gap-1">
                                          <span
                                            className="text-[10px] font-mono truncate"
                                            title={loc}
                                          >
                                            {loc}
                                          </span>
                                          <span
                                            className={`rounded-full px-1.5 py-px text-[9px] font-semibold uppercase tracking-wide ${chip.cls}`}
                                            data-testid={`line-matrix-chip-${i}-${loc}`}
                                            title={
                                              cellStatus === "unconfigured"
                                                ? "No fb_item_ordering_config row for this SKU"
                                                : cellStatus === "reorder"
                                                ? `Stock below reorder level (${fmtStock(reorderLevel)})`
                                                : cellStatus === "ok"
                                                ? `Stock at/above reorder level (${fmtStock(reorderLevel)})`
                                                : "Out of stock at this outlet"
                                            }
                                          >
                                            {chip.label}
                                          </span>
                                        </div>
                                        <div
                                          className={`text-[10px] tabular-nums ${stockNumberCls}`}
                                          data-testid={`line-matrix-stock-${i}-${loc}`}
                                        >
                                          stk {fmtStock(cellStock)}
                                          {reorderLevel != null && (
                                            <span className="text-muted-foreground">
                                              {" "}/ ROP {fmtStock(reorderLevel)}
                                            </span>
                                          )}
                                        </div>
                                        <Input
                                          type="number"
                                          min={0}
                                          step={fractional ? 0.001 : 1}
                                          value={cellVal}
                                          onChange={(e) => patchMatrixCell(i, loc, e.target.value)}
                                          disabled={!isDraft}
                                          className={`h-8 text-xs text-right tabular-nums ${
                                            cellStatus === "oos"
                                              ? "bg-rose-50 border-rose-200 focus-visible:ring-rose-300"
                                              : ""
                                          } ${
                                            // Phase 9.7.9-B — engine defaults
                                            // green/unconfigured cells to 0.
                                            // Dim the input so it's obvious
                                            // this is a default, not a hard
                                            // "do not order" signal — the
                                            // user can still type over it.
                                            cellVal === 0 &&
                                            (cellStatus === "ok" ||
                                              cellStatus === "unconfigured")
                                              ? "text-muted-foreground/70"
                                              : ""
                                          }`}
                                          data-testid={`line-matrix-qty-${i}-${loc}`}
                                          data-default-zero={
                                            cellVal === 0 &&
                                            (cellStatus === "ok" ||
                                              cellStatus === "unconfigured")
                                              ? "true"
                                              : "false"
                                          }
                                          aria-label={`Quantity for ${loc} (${chip.label})`}
                                          title={
                                            cellVal === 0 && cellStatus === "ok"
                                              ? "Auto-defaulted to 0 — stock is healthy. Type any qty to top-up."
                                              : cellVal === 0 && cellStatus === "unconfigured"
                                              ? "Auto-defaulted to 0 — no reorder rule for this SKU."
                                              : undefined
                                          }
                                        />
                                      </div>
                                    );
                                  })}
                                </div>
                                {/* Footer: Sum only (Distribute + Clear all removed in 9.7.7) */}
                                <div className="pt-2 border-t border-border/40">
                                  <span
                                    className="tabular-nums text-[11px]"
                                    data-testid={`line-matrix-sum-${i}`}
                                  >
                                    <span className="text-muted-foreground">Sum:</span>{" "}
                                    <span className="font-semibold">
                                      {fmtStock(qty)} {l.item_snapshot?.uom || ""}
                                    </span>
                                  </span>
                                </div>
                              </div>
                            </TableCell>
                          </TableRow>
                        )}
                        </Fragment>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Footer totals */}
            <div className="mt-4 border-t border-border/60 pt-3 flex justify-end text-sm">
              <div className="space-y-0.5 tabular-nums">
                <div className="flex gap-8">
                  <span className="text-muted-foreground">Basic</span>
                  <span className="font-medium w-32 text-right">{fmtINR(totals.total_basic_amount)}</span>
                </div>
                <div className="flex gap-8">
                  <span className="text-muted-foreground">Tax</span>
                  <span className="font-medium w-32 text-right">{fmtINR(totals.total_tax_amount)}</span>
                </div>
                <div className="flex gap-8 pt-1 border-t border-border/40 mt-1">
                  <span>Total</span>
                  <span className="font-semibold w-32 text-right" data-testid="totals-grand">{fmtINR(totals.total_amount)}</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Sidebar */}
        <div className="space-y-4">
          <Card>
            <CardHeader><div className="text-sm font-medium">Vendor</div></CardHeader>
            <CardContent className="text-xs space-y-1">
              <div className="text-sm font-medium">{po.vendor_snapshot?.name}</div>
              {po.vendor_snapshot?.gstin && <div>GSTIN: {po.vendor_snapshot.gstin}</div>}
              {po.vendor_snapshot?.phone && <div>Phone: {po.vendor_snapshot.phone}</div>}
              {po.vendor_snapshot?.email && <div>Email: {po.vendor_snapshot.email}</div>}
            </CardContent>
          </Card>

          {po.format === "location_wise" && po.ship_to_location_id && (
            <Card data-testid="ship-to-card">
              <CardHeader>
                <div className="text-sm font-medium">Ship-to · From</div>
              </CardHeader>
              <CardContent className="text-xs space-y-1.5">
                {shipToLoc ? (
                  <>
                    <div className="text-sm font-medium" data-testid="ship-to-name">
                      {shipToLoc.display_name || po.ship_to_location_id}
                    </div>
                    <div className="font-mono text-[10px] text-muted-foreground">
                      {po.ship_to_location_id}
                    </div>
                    {shipToLoc.company_name && (
                      <div className="text-muted-foreground">
                        {shipToLoc.company_name}
                      </div>
                    )}
                    {shipToLoc.gstin && (
                      <div data-testid="ship-to-gstin">
                        <span className="text-muted-foreground">GSTIN:</span>{" "}
                        <span className="font-mono">{shipToLoc.gstin}</span>
                      </div>
                    )}
                    {shipToLoc.phone && (
                      <div data-testid="ship-to-phone">
                        <span className="text-muted-foreground">Phone:</span>{" "}
                        {shipToLoc.phone}
                      </div>
                    )}
                    {shipToLoc.address && (
                      <div className="text-muted-foreground leading-snug" data-testid="ship-to-address">
                        {shipToLoc.address}
                      </div>
                    )}
                    {!shipToLoc.gstin && !shipToLoc.address && (
                      <div className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                        This outlet has no GSTIN / address on file yet.
                        Released PO will fall back to placeholder text.
                        <Link to="/tools/fnb-po/locations" className="underline ml-1">
                          Update in Locations →
                        </Link>
                      </div>
                    )}
                  </>
                ) : (
                  <code className="font-mono">{po.ship_to_location_id}</code>
                )}
              </CardContent>
            </Card>
          )}

          <Card>
            <CardHeader><div className="text-sm font-medium">Notes</div></CardHeader>
            <CardContent>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                disabled={!isDraft}
                className="text-xs"
                rows={3}
                placeholder="Internal notes…"
                data-testid="po-notes"
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader><div className="text-sm font-medium">Actions</div></CardHeader>
            <CardContent className="space-y-2">
              {isDraft && (
                <>
                  <Button
                    onClick={save} disabled={saving}
                    className="w-full" variant="outline"
                    data-testid="save-draft-btn"
                  >
                    {saving ? "Saving…" : "Save draft"}
                  </Button>
                  <Button
                    onClick={release} disabled={releasing || lines.length === 0}
                    className="w-full"
                    data-testid="release-btn"
                  >
                    {releasing ? "Releasing…" : "Release PO →"}
                  </Button>
                  <Button
                    onClick={cancelPO} variant="ghost"
                    className="w-full text-muted-foreground"
                    data-testid="cancel-btn"
                  >
                    Cancel PO
                  </Button>
                </>
              )}
              {po.status === "released" && (
                <>
                  <Button
                    onClick={markReceived}
                    className="w-full"
                    data-testid="mark-received-btn"
                  >
                    Mark received
                  </Button>
                  <Button
                    onClick={cancelPO} variant="ghost"
                    className="w-full text-muted-foreground"
                    data-testid="cancel-btn"
                  >
                    Cancel PO
                  </Button>
                </>
              )}
              {(po.status === "received" || po.status === "cancelled") && (
                <div className="text-xs text-muted-foreground text-center py-2">
                  This PO is closed. Duplicate to start a new one.
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
